"use client";

import { useState, useEffect, useCallback } from "react";
import { Heart } from "lucide-react";
import { useUser } from "@/lib/user-context";
import { LoginPromptModal, useLoginPrompt } from "@/components/common/login-prompt-modal";
import { addFavorite, removeFavorite, listFavorites } from "@/lib/auth-api";
import { toast } from "sonner";

export function FavoriteButton({ title }: { title: string }) {
  const { me } = useUser();
  const { modalState, requireLogin, closeModal } = useLoginPrompt();
  const [favorited, setFavorited] = useState(false);
  const [loading, setLoading] = useState(false);

  // 로그인 상태에서 즐겨찾기 여부 확인
  useEffect(() => {
    if (!me?.user) {
      setFavorited(false);
      return;
    }
    listFavorites("procedure")
      .then(({ favorites }) => {
        setFavorited(favorites.some((f) => f.target_id === title));
      })
      .catch(() => {});
  }, [me, title]);

  const doToggle = useCallback(async () => {
    if (loading) return;
    setLoading(true);
    try {
      if (favorited) {
        await removeFavorite("procedure", title);
        setFavorited(false);
        toast.success("즐겨찾기에서 제거했습니다");
      } else {
        await addFavorite("procedure", title);
        setFavorited(true);
        toast.success("즐겨찾기에 추가했습니다");
      }
    } catch {
      toast.error("오류가 발생했습니다");
    } finally {
      setLoading(false);
    }
  }, [favorited, loading, title]);

  const handleClick = useCallback(() => {
    requireLogin(!!me?.user, "favorite", doToggle);
  }, [me, requireLogin, doToggle]);

  return (
    <>
      <button
        type="button"
        onClick={handleClick}
        disabled={loading}
        className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-2 text-sm font-medium transition-all ${
          favorited
            ? "border-red-200 bg-red-50 text-red-600"
            : "border-gray-200 bg-white text-gray-600 hover:border-red-200 hover:bg-red-50"
        } disabled:opacity-50`}
      >
        <Heart className={`h-4 w-4 ${favorited ? "fill-current" : ""}`} />
        {favorited ? "즐겨찾기됨" : "즐겨찾기"}
      </button>
      <LoginPromptModal open={modalState.open} onClose={closeModal} trigger={modalState.trigger} />
    </>
  );
}
